import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogUnit from 'components/dialogs/DialogUnit';
import DialogUnitWithIcon from 'components/dialogs/DialogUnitWithIcon';
import { Grid } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import Buttons from 'components/buttons/Pressed';
import Typography from 'components/typography/Typography';
import Avatar from '@material-ui/core/Avatar';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InputFile from 'components/inputs/InputFile';
import { FormatDateToFront } from 'utils/dates/FormatDateToFront';
import { dateToBRWithHours } from 'utils/dates/DateToBrWithHours';
import { valueToMoneyFloat } from 'utils/general/valueToMoneyFloat';
import { moneyMask } from 'utils/masks/moneyMask';
import {
    translateCreditSolicitationsStatus,
    CreditSolicitationStatus
} from 'pages/quotasModule/dashboard/ScreenUtils';
import { screenStyles } from 'pages/quotasModule/dashboard/Styles';
import { dateToBR } from 'utils/dates/DateToBRr';

const DialogViewCreditSolicitations = props => {
    const styles = screenStyles();
    const [t] = useTranslation();

    const { open, setOpen, viewValues } = props;

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            maxWidth={'sm'}
            fullWidth
        >
            <DialogTitle>
                <Grid container item spacing={2} xs={12}>
                    <Grid item xs={11}>
                        <Typography
                            variant={'RobotoBold'}
                            fontSize={24}
                            textColor={'secondary'}
                        >
                            {`${t('GENERAL.view')} ${t(
                                'QUOTAS.creditSolicitation'
                            )}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton
                            color="#6D6F71"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>

            <DialogContent>
                <Grid container item spacing={1} xs={12}>
                    <Grid item xs={6}>
                        <DialogUnitWithIcon
                            head={t('GENERAL.collaborator').toUpperCase()}
                            Icon={
                                <Avatar
                                    className={styles.dialogViewBillAvatar}
                                    alt={viewValues.user.collaboratorName}
                                    src={
                                        process.env.REACT_APP_IMGSERVER +
                                        viewValues.user.imageName
                                    }
                                />
                            }
                            content={viewValues.user.collaboratorName}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <DialogUnit
                            head={t('GENERAL.solicitationDate').toUpperCase()}
                            content={dateToBRWithHours(
                                FormatDateToFront(viewValues.createdAt)
                            )}
                            marginTop={'16px'}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <DialogUnit
                            head={t('GENERAL.value').toUpperCase()}
                            content={`R$
                                    ${moneyMask(
                                        valueToMoneyFloat(viewValues.value)
                                    )}
                                    `}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <DialogUnit
                            head={t('GENERAL.dateOfPay').toUpperCase()}
                            content={dateToBR(
                                FormatDateToFront(viewValues.date)
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <div style={{ margin: '12px 0 16px' }}>
                            <DialogUnitWithIcon
                                head={t('GENERAL.status').toUpperCase()}
                                Icon={
                                    viewValues.status ===
                                    CreditSolicitationStatus.APPROVED ? (
                                        <CheckCircleIcon
                                            style={{
                                                color: '#7FE46B'
                                            }}
                                            fontSize="md"
                                        />
                                    ) : viewValues.status ===
                                      CreditSolicitationStatus.PENDING ? (
                                        <AccessTimeIcon fontSize="md" />
                                    ) : viewValues.status ===
                                          CreditSolicitationStatus.REFUSED ||
                                      viewValues.status ===
                                          CreditSolicitationStatus.CANCELLED ? (
                                        <ErrorIcon
                                            style={{
                                                color: '#F3222F'
                                            }}
                                            fontSize="md"
                                        />
                                    ) : null
                                }
                                content={translateCreditSolicitationsStatus(
                                    viewValues.status
                                )}
                            />
                        </div>
                    </Grid>

                    {viewValues.receiptFilename && viewValues.receiptPath && (
                        <Grid item xs={12}>
                            <div style={{ marginBottom: '16px' }}>
                                <InputFile
                                    context={'view'}
                                    fileName={viewValues.receiptFilename}
                                    filePath={viewValues.receiptPath}
                                    label={t('GENERAL.proof')}
                                    placeholder={t('GENERAL.noFile')}
                                />
                            </div>
                        </Grid>
                    )}

                    {viewValues.description ? (
                        <Grid item xs={12}>
                            <DialogUnit
                                head={t('GENERAL.observation').toUpperCase()}
                                content={viewValues.description}
                            />
                        </Grid>
                    ) : null}

                    {viewValues.status !== CreditSolicitationStatus.PENDING &&
                    viewValues.status !== CreditSolicitationStatus.CANCELLED ? (
                        <>
                            {viewValues.operator ? (
                                <Grid item xs={6}>
                                    <DialogUnitWithIcon
                                        head={t(
                                            'GENERAL.operator'
                                        ).toUpperCase()}
                                        Icon={
                                            <Avatar
                                                className={
                                                    styles.dialogViewBillAvatar
                                                }
                                                alt={
                                                    viewValues.operator
                                                        .shortname
                                                }
                                                src={
                                                    process.env
                                                        .REACT_APP_IMGSERVER +
                                                    viewValues.operator
                                                        .imageName
                                                }
                                            />
                                        }
                                        content={viewValues.operator.shortname}
                                    />
                                </Grid>
                            ) : null}

                            <Grid item xs={6}>
                                <DialogUnit
                                    head={
                                        viewValues.status ===
                                        CreditSolicitationStatus.CANCELLED
                                            ? t(
                                                  'GENERAL.cancellationDate'
                                              ).toUpperCase()
                                            : t(
                                                  'GENERAL.operationDate'
                                              ).toUpperCase()
                                    }
                                    content={dateToBRWithHours(
                                        FormatDateToFront(viewValues.closedAt)
                                    )}
                                    marginTop={'16px'}
                                />
                            </Grid>

                            {viewValues.justification ? (
                                <Grid item xs={12}>
                                    <DialogUnit
                                        head={t('GENERAL.reason').toUpperCase()}
                                        content={viewValues.justification}
                                    />
                                </Grid>
                            ) : null}
                        </>
                    ) : null}
                </Grid>
            </DialogContent>

            <DialogActions style={{ padding: '24px 30px' }}>
                <Buttons
                    onClick={handleClose}
                    tipo={'BN-blue'}
                    conteudo={t('GENERAL.close').toUpperCase()}
                />
            </DialogActions>
        </Dialog>
    );
};

export default DialogViewCreditSolicitations;
